import {
  Component,
  input,
  output,
  signal,
  ChangeDetectionStrategy,
  effect,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DateInputsModule } from '@progress/kendo-angular-dateinputs';

export interface DateFilterOption {
  text: string;
  value: string;
}

export interface DateFilterChange {
  dateFilterFrom: Date | null;
  dateFilterTo: Date | null;
  dateFilterField: string | null;
  includeAllRequestType: string | null;
}

/**
 * Reusable date filter component with dropdown and date range picker.
 * Only emits filter changes when:
 * - Date type changes AND dates are populated
 * - Dates change (after the date range dialog closes)
 *
 * Hides the date range picker when "All Dates" is selected.
 */
@Component({
  selector: 'ups-date-filter',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, DateInputsModule],
  template: `
    <div class="d-flex align-items-center gap-2">
      <!-- Date Filter Type Dropdown -->
      <select
        [(ngModel)]="selectedDateFilter"
        (ngModelChange)="onDateFilterFieldChange($event)"
        class="form-select"
        [style.width.px]="dropdownWidth()"
        [title]="dropdownTitle()"
      >
        <option [ngValue]="null">{{ allDatesLabel() }}</option>
        @for (option of dateFilterOptions(); track option.value) {
          <option [ngValue]="option.value">{{ option.text }}</option>
        }
      </select>

      <!-- Date Range - only shown when a date filter is selected -->
      @if (selectedDateFilter() !== null) {
        <kendo-daterange [style.width.px]="dateRangeWidth()">
          <kendo-dateinput
            kendoDateRangeStartInput
            [(value)]="range().start"
            (valueChange)="
              onDateRangeChange({ start: $event, end: range().end })
            "
            [placeholder]="startPlaceholder()"
          ></kendo-dateinput>
          <kendo-dateinput
            kendoDateRangeEndInput
            [(value)]="range().end"
            (valueChange)="
              onDateRangeChange({ start: range().start, end: $event })
            "
            [placeholder]="endPlaceholder()"
          ></kendo-dateinput>
        </kendo-daterange>
      }
    </div>
  `,
  styles: [``],
})
export class DateFilterComponent {
  // Configuration inputs
  readonly dateFilterOptions = input.required<DateFilterOption[]>();
  readonly allDatesLabel = input<string>('All Dates');
  readonly dropdownTitle = input<string>('Select date field to filter by');
  readonly dropdownWidth = input<number>(160);
  readonly dateRangeWidth = input<number>(280);
  readonly startPlaceholder = input<string>('From');
  readonly endPlaceholder = input<string>('To');

  // Initial values (for URL-driven filters)
  readonly initialDateFilterFrom = input<Date | null>(null);
  readonly initialDateFilterTo = input<Date | null>(null);
  readonly initialDateFilterField = input<string | null>(null);

  // Output event
  readonly filterChanged = output<DateFilterChange>();

  // Internal state
  public selectedDateFilter = signal<string | null>(null);
  public range = signal<{ start: Date | null; end: Date | null }>({
    start: null,
    end: null,
  });

  // Track if we're in the initial setup phase
  private isInitializing = true;

  constructor() {
    // Initialize from input values
    effect(() => {
      if (this.isInitializing) {
        const from = this.initialDateFilterFrom();
        const to = this.initialDateFilterTo();
        const field = this.initialDateFilterField();

        if (from && to && field) {
          this.selectedDateFilter.set(field);
          this.range.set({ start: from, end: to });

          // Emit the initial filter so the grid applies it on load
          this.emitFilterChange();
        }

        // Mark initialization as complete after first run
        this.isInitializing = false;
      }
    });
  }

  public onDateFilterFieldChange(fieldValue: string | null): void {
    this.selectedDateFilter.set(fieldValue);

    // If changing to "All Dates", clear the date range and emit immediately
    if (fieldValue === null) {
      this.range.set({ start: null, end: null });
      this.emitFilterChange();
      return;
    }

    // If changing to a specific field and dates are already populated, emit the change
    const currentRange = this.range();
    if (currentRange.start && currentRange.end) {
      this.emitFilterChange();
    }
    // If dates are NOT populated, don't emit yet - wait for user to pick dates
  }

  public onDateRangeChange(newRange: {
    start: Date | null;
    end: Date | null;
  }): void {
    this.range.set(newRange);

    // Only emit if BOTH dates are populated
    // This waits for the date range dialog to close before emitting
    if (newRange.start && newRange.end) {
      this.emitFilterChange();
    }
  }

  private emitFilterChange(): void {
    const filterField = this.selectedDateFilter();
    const currentRange = this.range();

    this.filterChanged.emit({
      dateFilterFrom: currentRange.start,
      dateFilterTo: currentRange.end,
      dateFilterField: filterField,
      includeAllRequestType: null,
    });
  }

  /**
   * Public method to reset the date filter (for "Clear Filters" buttons)
   */
  public reset(): void {
    this.selectedDateFilter.set(null);
    this.range.set({ start: null, end: null });
    this.emitFilterChange();
  }
}
